// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: the Konvo summary card in the email reader. Opening a mail asks the
// server (POST /konvo/mail/messages/:id/summary) for one or two sentences on
// what it needs from the reader plus up to three actions. The server reads
// the mail itself, runs the reader's assistant once and caches the answer, so
// this store only keeps what came back for the session and paces requests:
// a short delay so flicking through the list does not start a run per mail,
// and a retry while another mail's summary is still being written.
// Summaries can be switched off (Assistant settings) and a card can be hidden
// for one mail; both are saved on this device, like the chat suggestions, and
// neither asks the server, so a hidden card costs no assistant run.

import {Endpoints} from '@app/features/app/constants/Endpoints';
import {type MailMeetingAction, parseMeetingAction} from '@app/features/konvo_mail/state/MailMeeting';
import type {TaskSuggestion} from '@app/features/messaging/components/modals/KonvoCreateTaskModal';
import AppStorage from '@app/features/platform/state/PersistentStorage';
import {http} from '@app/features/platform/transport/RestTransport';
import {makeAutoObservable} from 'mobx';

export type MailSummaryAction =
	/** `template` (servers with the time picker): the body with [[time]] / [[backup]] still in it. */
	| {kind: 'reply'; body: string; template?: string}
	/** `attendees`: addresses on the mail to invite when the reader sends the invite; absent for a hold. */
	| {kind: 'event'; title: string; start: string; end: string; attendees?: Array<string>}
	| MailMeetingAction
	| {
			kind: 'task';
			name: string;
			description: string;
			due: string | null;
			priority: number | null;
			/** The finished create-task form (list and assignee settled server-side). */
			draft?: TaskSuggestion;
	  };

export interface MailSummaryResult {
	/** One short line on what the mail needs (the peek beside the corner assistant). */
	headline: string;
	/** One or two sentences; key dates are wrapped in `**`. */
	summary: string;
	actions: Array<MailSummaryAction>;
}

export type MailSummaryState =
	| {status: 'loading'}
	| {status: 'ready'; result: MailSummaryResult}
	/** The mail needs nothing from the reader, or the assistant could not say: no card. */
	| {status: 'empty'};

interface SummaryResponse {
	headline?: string | null;
	summary?: string | null;
	actions?: Array<MailSummaryAction>;
	busy?: boolean;
}

/** Long enough that arrowing through the list does not start a run per mail. */
const REQUEST_DELAY_MS = 450;
const BUSY_RETRY_MS = 4000;
const MAX_BUSY_RETRIES = 20;
const REQUEST_TIMEOUT_MS = 90_000;
const MAX_ENTRIES = 150;

const KINDS = new Set(['reply', 'event', 'task', 'meeting']);

const ENABLED_KEY = 'konvo.assistant.mailSummaries';
const DISMISSED_KEY = 'konvo.assistant.mailSummaries.dismissed';
const MAX_DISMISSED = 300;

function readDismissed(): Array<string> {
	try {
		const parsed: unknown = JSON.parse(AppStorage.getItem(DISMISSED_KEY) ?? '[]');
		return Array.isArray(parsed) ? parsed.filter((id): id is string => typeof id === 'string') : [];
	} catch {
		return [];
	}
}

/** Pure: the server's answer to a card state; anything unexpected hides the card. */
export function summaryStateFrom(body: SummaryResponse | null | undefined): MailSummaryState {
	const summary = typeof body?.summary === 'string' ? body.summary.trim() : '';
	if (!summary) return {status: 'empty'};
	const actions = (Array.isArray(body?.actions) ? body.actions : [])
		.filter((action) => action && typeof action === 'object' && KINDS.has(action.kind))
		.flatMap((action): Array<MailSummaryAction> => {
			if (action.kind !== 'meeting') return [action];
			const meeting = parseMeetingAction(action);
			return meeting ? [meeting] : [];
		})
		.slice(0, 3);
	// Servers from before headlines: the summary's first sentence.
	const headline =
		(typeof body?.headline === 'string' ? body.headline.trim() : '') ||
		(summary.replace(/\*\*/g, '').match(/^[^.!?]+/)?.[0] ?? summary).trim();
	return {status: 'ready', result: {headline, summary, actions}};
}

export class MailSummaryStore {
	/** Off in Assistant settings: no card and no request. */
	enabled = AppStorage.getItem(ENABLED_KEY) !== 'false';
	entries = new Map<string, MailSummaryState>();
	/** Mails whose card was hidden with its close button, oldest first. */
	dismissed = new Set<string>(readDismissed());
	private timers = new Map<string, ReturnType<typeof setTimeout>>();
	private busyTries = new Map<string, number>();
	private inFlight = new Set<string>();

	constructor() {
		makeAutoObservable<this, 'timers' | 'busyTries' | 'inFlight'>(
			this,
			{timers: false, busyTries: false, inFlight: false},
			{autoBind: true},
		);
	}

	get(messageId: string): MailSummaryState | null {
		return this.entries.get(messageId) ?? null;
	}

	setEnabled(value: boolean): void {
		this.enabled = value;
		AppStorage.setItem(ENABLED_KEY, String(value));
		if (!value) {
			for (const timer of this.timers.values()) clearTimeout(timer);
			this.timers.clear();
			this.busyTries.clear();
			this.entries.clear();
		}
	}

	isDismissed(messageId: string): boolean {
		return this.dismissed.has(messageId);
	}

	/** Hide the card for this mail, on this device. */
	dismiss(messageId: string): void {
		this.dismissed.add(messageId);
		while (this.dismissed.size > MAX_DISMISSED) {
			const oldest = this.dismissed.values().next().value;
			if (oldest === undefined) break;
			this.dismissed.delete(oldest);
		}
		AppStorage.setItem(DISMISSED_KEY, JSON.stringify([...this.dismissed]));
		this.close(messageId);
		this.entries.delete(messageId);
	}

	/**
	 * The reader opened this mail. `isCurrent` tells a delayed request or a
	 * retry whether the mail is still open; if not, it quietly gives up.
	 */
	open(messageId: string, isCurrent: () => boolean): void {
		if (!this.enabled || this.dismissed.has(messageId) || this.entries.has(messageId)) return;
		this.set(messageId, {status: 'loading'});
		this.schedule(messageId, REQUEST_DELAY_MS, isCurrent);
	}

	/** The mail was closed: a request that has not gone out yet is dropped, so reopening asks again. */
	close(messageId: string): void {
		const timer = this.timers.get(messageId);
		if (!timer || this.inFlight.has(messageId)) return;
		clearTimeout(timer);
		this.timers.delete(messageId);
		this.busyTries.delete(messageId);
		if (this.entries.get(messageId)?.status === 'loading') this.entries.delete(messageId);
	}

	private set(messageId: string, state: MailSummaryState): void {
		this.entries.delete(messageId);
		this.entries.set(messageId, state);
		while (this.entries.size > MAX_ENTRIES) {
			const oldest = this.entries.keys().next().value;
			if (oldest === undefined) break;
			this.entries.delete(oldest);
		}
	}

	private schedule(messageId: string, delay: number, isCurrent: () => boolean): void {
		const existing = this.timers.get(messageId);
		if (existing) clearTimeout(existing);
		this.timers.set(
			messageId,
			setTimeout(() => {
				this.timers.delete(messageId);
				void this.request(messageId, isCurrent);
			}, delay),
		);
	}

	private async request(messageId: string, isCurrent: () => boolean): Promise<void> {
		if (!isCurrent()) {
			this.forget(messageId);
			return;
		}
		this.inFlight.add(messageId);
		try {
			const response = await http.post<SummaryResponse>(Endpoints.KONVO_MAIL_MESSAGE_SUMMARY(messageId), {
				timeoutMs: REQUEST_TIMEOUT_MS,
				mode: 'silent',
			});
			if (response.ok && response.body?.busy) {
				const tries = (this.busyTries.get(messageId) ?? 0) + 1;
				this.busyTries.set(messageId, tries);
				if (tries <= MAX_BUSY_RETRIES && isCurrent()) this.schedule(messageId, BUSY_RETRY_MS, isCurrent);
				else this.forget(messageId);
				return;
			}
			this.busyTries.delete(messageId);
			// Errors (assistant disconnected, Graph down, a failed run) hide the card and are not
			// remembered, so opening the mail again later asks again.
			if (response.ok) this.set(messageId, summaryStateFrom(response.body));
			else this.forget(messageId);
		} catch {
			this.forget(messageId);
		} finally {
			this.inFlight.delete(messageId);
		}
	}

	private forget(messageId: string): void {
		this.busyTries.delete(messageId);
		this.entries.delete(messageId);
	}
}

export default new MailSummaryStore();
